import {MithrilComponent} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {classes, text_before} from '@bitstillery/common/lib/utils'
import {current_account_slug, type AccountSlug} from '@bitstillery/common/lib/account'

export interface LinkAttrs {
    account?: AccountSlug | string | null
    /** Overrides the route-derived active flag, for a menu whose entries share one path and
     *  differ only in their query params — there, path matching flags every entry as active. */
    active?: boolean
    class?: string
    href: string
    onclick?: (e: MouseEvent) => unknown
    target?: string
    className?: string
    disabled?: boolean
    type?: string
    tabindex?: number
    remount?: boolean
}

export class Link extends MithrilComponent<LinkAttrs> {
    get_link_target(link_attrs: LinkAttrs, is_external: boolean): string {
        if (link_attrs.target) {
            return link_attrs.target
        }

        if (is_external) {
            return '_blank'
        }

        if (process.env.MSI_PACKAGE === 'discover' && link_attrs.account) {
            return current_account_slug() === link_attrs.account ? '_self' : '_blank'
        }

        return '_self'
    }

    add_account_to_href(href: string, account: string): string {
        return `${href}${href.includes('?') ? '&' : '?'}account=${account}`
    }

    view(vnode: m.Vnode<LinkAttrs>): m.Children {
        let href = encodeURI(vnode.attrs.href)
        const is_external_link = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')
        const target = this.get_link_target(vnode.attrs, is_external_link)

        if (process.env.MSI_PACKAGE === 'discover' && !is_external_link && !href.includes('account=')) {
            href = this.add_account_to_href(href, vnode.attrs.account ?? current_account_slug())
        }

        if (is_external_link) {
            // External links cannot be handled by m.route.Link
            return (
                <a
                    href={href}
                    className={classes(
                        'c-link',
                        'no-click',
                        `type-${vnode.attrs.type ? vnode.attrs.type : 'info'}`,
                        vnode.attrs.class ?? vnode.attrs.className,
                    )}
                    rel={'noreferrer noopener'}
                    disabled={vnode.attrs.disabled}
                    target={target}
                >
                    {vnode.children}
                </a>
            )
        }

        // Match the current route's path (query/hash ignored) so nav chrome can flag the active link;
        // a sub-path (e.g. `/shop/…`) keeps its parent `/shop` link active.
        const current_path = text_before(m.route.get(), '?')
        const target_path = text_before(text_before(vnode.attrs.href, '?'), '#')
        const is_active = vnode.attrs.active ?? (current_path === target_path || current_path.startsWith(`${target_path}/`))

        return m(
            m.route.Link,
            {
                className: classes(
                    'c-link',
                    'no-click',
                    `type-${vnode.attrs.type ? vnode.attrs.type : 'info'}`,
                    {active: is_active},
                    vnode.attrs.class ?? vnode.attrs.className,
                ),
                disabled: vnode.attrs.disabled,
                href: href,
                options: {remount: vnode.attrs.remount ?? false},
                onclick: (e: MouseEvent) => {
                    e.stopPropagation()
                    if (vnode.attrs.onclick) {
                        vnode.attrs.onclick(e)
                    }
                },
                rel: 'noreferrer noopener',
                // `?? -1`, not a truthiness check: `tabindex={0}` (put the link in the tab order) is the
                // one value a caller has to spell out, and the falsy test silently turned it into -1.
                tabindex: vnode.attrs.tabindex ?? -1,
                target: target,
            },
            vnode.children,
        )
    }
}
